import React, { useState } from "react";
import { array } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { Padding } from "../../components/BuildingBlocks/Padding";
import { CRMCardOutside } from "../../components/CRMCardOutside/CRMCardOutside";
import { CRMTitleSection } from "../../components/CRMTitleSection/CRMTitleSection";
import { CRMSpacing } from "../../models/CRMSpacing";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { snakeCaseToCopyText } from "../../../../shared/src/util";
import { TAdminUserEditable } from "../../../../domain/codecs/formEditable/AdminUserEditable";
import { CRMAutosaveIndicatorWrap } from "../../components/CRM/CRMAutosaveIndicatorWrap/CRMAutosaveIndicatorWrap";
import { AdminUser } from "../../../../domain/codecs/User";
import { CRMCodecEditForm } from "../../components/CRMCodecEditForm/CRMCodecEditForm";
import { CRMLoadingBar } from "../../components/Simple/CRMLoadingBar/CRMLoadingBar";
import { SimpleGrid } from "../../components/BuildingBlocks/SimpleGrid";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { CRMDropdownFormInput } from "../../components/CRMFormInputs/CRMDropdownFormInput";
import { TInternalScope } from "../../../../domain/codecs/InternalScope";
import { CRMButtonTertiaryColorable } from "../../components/CRMButtonTertiaryColorable/CRMButtonTertiaryColorable";
import { CRMColors } from "../../models/CRMColors";
import { CRMBlockPopover } from "../../components/CRM/CRMBlock/CRMBlockPopover/CRMBlockPopover";
import { CRMSingleViewHeader } from "../../components/Simple/CRMSingleViewHeader/CRMSingleViewHeader";
import { formOperation } from "../../wrappers/formOperation";
import { GetUserDeletionBlockersFormIO, TGetUserDeletionBlockersFormIO } from "../../../../domain/codecs/formIO/GetUserDeletionBlockersFormIO";
import { AutoReassignUserWorkFormIO, TAutoReassignUserWorkFormIO } from "../../../../domain/codecs/formIO/AutoReassignUserWorkFormIO";
import { TextColor } from "../../components/BuildingBlocks/TextColor";

const STAFF_ROLE_OPTIONS: Array<{ value: TInternalScope; label: string }> = [
    { value: "admin", label: "Admin" },
    { value: "sail_homes_staff", label: "Sail Homes Staff" },
    { value: "sail_legal_authoriser_staff", label: "Sail Legal Authoriser Staff" },
    { value: "sail_legal_non_authoriser_staff", label: "Sail Legal Non Authoriser Staff" },
];

const HIDDEN_FIELDS: Array<
    "id" | "jobs" | "staff_email" | "company" | "deleted_at"
> = [
    "id",
    "jobs",
    "staff_email",
    "company",
    "deleted_at",
];

const groupUsersByCompany = (users: Array<TAdminUserEditable>): Array<[string, Array<TAdminUserEditable>]> =>
    pipe(
        users,
        array.reduce<TAdminUserEditable, Record<string, Array<TAdminUserEditable>>>(
            {},
            (acc, user) => {
                const company = user.edited.company;
                return {
                    ...acc,
                    [company]: [...(acc[company] || []), user],
                };
            },
        ),
        Object.entries,
        (entries) => entries.sort(([a], [b]) => a.localeCompare(b)),
    );

type TBlockerRow = { label: string; count: number };

const blockerRowsFromOutput = (b: TGetUserDeletionBlockersFormIO["output"]): Array<TBlockerRow> => [
    { label: "Open cases as handler", count: b.open_cases_as_handler },
    { label: "Open cases as supervisor", count: b.open_cases_as_supervisor },
    { label: "Open ad-hoc jobs", count: b.open_ad_hoc_jobs },
    { label: "Unresolved emails in inbox", count: b.unresolved_emails },
    { label: "Open roadblocks assigned", count: b.open_roadblocks },
    { label: "Delegated inbox owner of", count: b.delegated_inbox_owner_count },
];

const singletonFlagsFromOutput = (b: TGetUserDeletionBlockersFormIO["output"]): Array<string> => {
    const flags: Array<string> = [];
    if (b.is_only_fallback_roadblocks_assignee) flags.push("This is the ONLY fallback roadblocks assignee");
    if (b.is_only_sign_off_authoriser) flags.push("This is the ONLY user who can sign off roadblocks");
    if (b.is_only_mlro) flags.push("This is the ONLY user with MLRO access");
    return flags;
};

const hasAnyBlockers = (b: TGetUserDeletionBlockersFormIO["output"]): boolean =>
    blockerRowsFromOutput(b).some((r) => r.count > 0)
    || singletonFlagsFromOutput(b).length > 0;

const hasManualOnlyBlockers = (b: TGetUserDeletionBlockersFormIO["output"]): boolean =>
    b.open_cases_as_handler > 0
    || b.open_cases_as_supervisor > 0
    || b.open_ad_hoc_jobs > 0
    || b.delegated_inbox_owner_count > 0
    || singletonFlagsFromOutput(b).length > 0;

const hasAutoHandleableBlockers = (b: TGetUserDeletionBlockersFormIO["output"]): boolean =>
    b.unresolvable_roadblocks === 0
    && !hasManualOnlyBlockers(b)
    && (b.unresolved_emails > 0 || b.open_roadblocks > 0);

export const CRMAdminEditStaffUserContainer = (props: TContainerStateProps): JSX.Element => {

    const pageStatus = props.state.forms.homes_admin_page.data.status;
    const adminUsers = props.state.forms.homes_admin_page.data.output.adminUsers;

    const [deleteTarget, setDeleteTarget] = useState<TAdminUserEditable | null>(null);
    const [blockers, setBlockers] = useState<TGetUserDeletionBlockersFormIO["output"] | null>(null);
    const [blockerStatus, setBlockerStatus] = useState<"untouched" | "loading" | "success" | "failure">("untouched");
    const [reassignStatus, setReassignStatus] = useState<"untouched" | "loading" | "success" | "failure">("untouched");
    const [reassignResult, setReassignResult] = useState<TAutoReassignUserWorkFormIO["output"] | null>(null);
    // Two-click confirm rather than the CRM countdown pattern: a mis-click here strands a
    // colleague out of the CRM until someone re-enrols them.
    const [resettingUserId, setResettingUserId] = useState<string | null>(null);

    const hasAuthenticator = (userId: string): boolean =>
        props.state.forms.homes_admin_page.data.output.staffWithAuthenticator.includes(userId);

    const activeUsers = pipe(
        adminUsers,
        array.filter((user) => user.edited.deleted_at === null),
    );

    const deletedUsers = pipe(
        adminUsers,
        array.filter((user) => user.edited.deleted_at !== null),
    );

    const onUserChange = (updatedUser: TAdminUserEditable) => {
        const updatedUsers = pipe(
            adminUsers,
            array.map((user) =>
                user.original.id === updatedUser.original.id
                    ? updatedUser
                    : user
            ),
        );
        props.dispatch({
            type: "HOMES_ADMIN_USERS_CHANGE",
            payload: updatedUsers,
        });
    };

    const onDeleteClick = async (user: TAdminUserEditable) => {
        setDeleteTarget(user);
        setBlockers(null);
        setReassignStatus("untouched");
        setReassignResult(null);
        await refreshBlockers(user.original.id);
    };

    const closePopup = () => {
        setDeleteTarget(null);
        setBlockers(null);
        setBlockerStatus("untouched");
        setReassignStatus("untouched");
        setReassignResult(null);
    };

    const refreshBlockers = async (userId: string) => {
        setBlockerStatus("loading");
        const form = GetUserDeletionBlockersFormIO.newDefault();
        form.input = { user_id: userId };
        const res = await formOperation("GetUserDeletionBlockers", form);
        if (res.status === "success") {
            setBlockers(res.output);
            setBlockerStatus("success");
        } else {
            setBlockerStatus("failure");
        }
    };

    const onAutoReassign = async () => {
        if (!deleteTarget) return;
        setReassignStatus("loading");
        const form = AutoReassignUserWorkFormIO.newDefault();
        form.input = { user_id: deleteTarget.original.id };
        const res = await formOperation("AutoReassignUserWork", form);
        if (res.status === "success") {
            setReassignResult(res.output);
            setReassignStatus("success");
            await refreshBlockers(deleteTarget.original.id);
        } else {
            setReassignStatus("failure");
        }
    };

    const onConfirmDelete = () => {
        if (!deleteTarget) return;
        onUserChange({
            ...deleteTarget,
            status: "requiresSubmission",
            edited: {
                ...deleteTarget.edited,
                deleted_at: new Date().toISOString(),
            },
        });
        closePopup();
    };

    const onRestore = (user: TAdminUserEditable) => {
        onUserChange({
            ...user,
            status: "requiresSubmission",
            edited: {
                ...user.edited,
                deleted_at: null,
            },
        });
    };

    const renderUserCard = (user: TAdminUserEditable, isDeleted: boolean) => (
        <CRMCardOutside
            key={user.original.id}
            borderRounding="none"
            backgroundColor={isDeleted ? "neutral-8" : undefined}
        >
            <Padding spacing={CRMSpacing.MEDIUM}>
                <CRMAutosaveIndicatorWrap status={user.status}>
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        {/* User name and role */}
                        <SpacingColumn spacing={CRMSpacing.TINY}>
                            <CRMParagraph fontSize={CRMFontSizes.MED}>
                                <WeightBold>
                                    {user.edited.first_name} {user.edited.last_name}
                                </WeightBold>
                            </CRMParagraph>
                            <CRMParagraph>
                                {snakeCaseToCopyText(user.edited.user_role)}
                                {user.edited.staff_email && ` · ${user.edited.staff_email}`}
                            </CRMParagraph>

                            {/* The recovery path for a lost or wiped device. There is no
                                self-serve fallback once enrolled, so this is the only way back
                                in -- see docs/adr/0025-staff-second-factor-is-step-up-protected.md */}
                            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                                <CRMParagraph>
                                    {hasAuthenticator(user.edited.id)
                                        ? "Authenticator app: set up"
                                        : "Authenticator app: not set up"}
                                </CRMParagraph>

                                {hasAuthenticator(user.edited.id) && !isDeleted &&
                                    <CRMButtonTertiaryColorable
                                        label={resettingUserId === user.edited.id ? "Reset it?" : "Reset"}
                                        textAlignment="center"
                                        textColor={CRMColors.NEUTRAL_PAPER}
                                        backgroundColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}
                                        backgroundColorHover={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2}
                                        onClick={() => {
                                            if (resettingUserId === user.edited.id) {
                                                setResettingUserId(null);
                                                props.dispatch({
                                                    type: "CRM_ADMIN_RESET_USER_AUTHENTICATOR",
                                                    payload: user.edited.id,
                                                });
                                            } else {
                                                setResettingUserId(user.edited.id);
                                            }
                                        }}
                                    />
                                }
                            </SpacingRow>
                        </SpacingColumn>

                        {/* Editable fields */}
                        <CRMCodecEditForm
                            codec={AdminUser}
                            model={user.edited}
                            validationErrors={user.validationErrors}
                            columns={1}
                            disabled={isDeleted}
                            hideFields={HIDDEN_FIELDS}
                            overrideComps={{
                                user_role: (overrideProps) =>
                                    <CRMDropdownFormInput
                                        {...overrideProps}
                                        model={overrideProps.model as TInternalScope}
                                        onChange={overrideProps.onChange as (v: TInternalScope) => void}
                                        options={STAFF_ROLE_OPTIONS}
                                    />,
                            }}
                            onChange={(edited) =>
                                onUserChange({
                                    ...user,
                                    status: "requiresSubmission",
                                    edited: {
                                        ...user.edited,
                                        ...edited,
                                    },
                                })
                            }
                        />

                        {/* Delete / Restore action */}
                        {isDeleted ?
                            <CRMButtonTertiaryColorable
                                label="Restore user"
                                textAlignment="center"
                                textColor={CRMColors.NEUTRAL_PAPER}
                                backgroundColor={CRMColors.PRIMARY_2}
                                backgroundColorHover={CRMColors.PRIMARY_4}
                                onClick={() => onRestore(user)}
                            />
                            :
                            <CRMButtonTertiaryColorable
                                label="Delete user"
                                textAlignment="center"
                                textColor={CRMColors.NEUTRAL_PAPER}
                                backgroundColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}
                                backgroundColorHover={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2}
                                onClick={() => onDeleteClick(user)}
                            />
                        }
                    </SpacingColumn>
                </CRMAutosaveIndicatorWrap>
            </Padding>
        </CRMCardOutside>
    );

    const activeUsersByCompany = groupUsersByCompany(activeUsers);

    const renderDeletePopup = () => {
        if (!deleteTarget) return null;
        const targetName = `${deleteTarget.edited.first_name} ${deleteTarget.edited.last_name}`;
        const blocked = blockers !== null && hasAnyBlockers(blockers);

        const canAutoHandle = blockers !== null && hasAutoHandleableBlockers(blockers);

        return (
            <CRMBlockPopover width="500px" display="flex" justifyContent="center">
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <CRMSingleViewHeader
                        title={`Delete ${targetName}?`}
                        edge="border"
                    />

                    <Padding type="custom" spacing={`${CRMSpacing.MEDIUM} ${CRMSpacing.MEDIUM} 0`}>
                        {blockerStatus === "loading" && <>
                            <CRMParagraph>Checking what would break...</CRMParagraph>
                            <CRMLoadingBar duration={3000} />
                        </>}

                        {blockerStatus === "failure" &&
                            <CRMParagraph>
                                <TextColor color={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}>
                                    Could not check blockers. Please try again.
                                </TextColor>
                            </CRMParagraph>
                        }

                        {blockerStatus === "success" && blockers && !blocked &&
                            <CRMParagraph>
                                Nothing currently assigned to this user. This will soft-delete them and they can be restored later.
                            </CRMParagraph>
                        }

                        {blockerStatus === "success" && blockers && blocked && <>
                            <CRMParagraph>
                                <WeightBold>This user still has active work assigned. Reassign these before deleting:</WeightBold>
                            </CRMParagraph>
                            <Padding type="vertical" spacing={CRMSpacing.SMALL}>
                                <SpacingColumn spacing={CRMSpacing.TINY}>
                                    {pipe(
                                        blockerRowsFromOutput(blockers),
                                        array.filter((r) => r.count > 0),
                                        array.map((r) => (
                                            <CRMParagraph key={r.label}>
                                                · {r.label}: <WeightBold>{r.count}</WeightBold>
                                            </CRMParagraph>
                                        )),
                                    )}
                                    {pipe(
                                        singletonFlagsFromOutput(blockers),
                                        array.map((f) => (
                                            <CRMParagraph key={f}>
                                                <TextColor color={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}>
                                                    · {f}
                                                </TextColor>
                                            </CRMParagraph>
                                        )),
                                    )}
                                </SpacingColumn>
                            </Padding>
                            {blockers.unresolvable_roadblocks > 0 &&
                                <Padding type="vertical" spacing={CRMSpacing.SMALL}>
                                    <CRMParagraph>
                                        <TextColor color={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}>
                                            <WeightBold>
                                                {blockers.unresolvable_roadblocks} roadblock{blockers.unresolvable_roadblocks === 1 ? "" : "s"} can't be auto-reassigned
                                            </WeightBold>
                                            {" "}— either this user is the case handler (reassign the case first), or there's no case handler and no fallback assignee configured.
                                        </TextColor>
                                    </CRMParagraph>
                                </Padding>
                            }
                            {hasManualOnlyBlockers(blockers) &&
                                <Padding type="vertical" spacing={CRMSpacing.SMALL}>
                                    <CRMParagraph>
                                        <TextColor color={CRMColors.NEUTRAL_2}>
                                            Auto-handle only covers emails and roadblocks. The other blockers above need manual reassignment before this user can be deleted.
                                        </TextColor>
                                    </CRMParagraph>
                                </Padding>
                            }
                        </>}

                        {reassignStatus === "success" && reassignResult && <>
                            <Padding type="vertical" spacing={CRMSpacing.SMALL}>
                                <SpacingColumn spacing={CRMSpacing.TINY}>
                                    <CRMParagraph>
                                        <WeightBold>Auto-handled:</WeightBold>
                                    </CRMParagraph>
                                    <CRMParagraph>
                                        · {reassignResult.emails_unassigned} email{reassignResult.emails_unassigned === 1 ? "" : "s"} moved back to triage
                                    </CRMParagraph>
                                    {reassignResult.roadblocks_reassigned_to_case_handler > 0 &&
                                        <CRMParagraph>
                                            · {reassignResult.roadblocks_reassigned_to_case_handler} roadblock{reassignResult.roadblocks_reassigned_to_case_handler === 1 ? "" : "s"} reassigned to case handler
                                        </CRMParagraph>
                                    }
                                    {reassignResult.roadblocks_reassigned_to_fallback > 0 &&
                                        <CRMParagraph>
                                            · {reassignResult.roadblocks_reassigned_to_fallback} roadblock{reassignResult.roadblocks_reassigned_to_fallback === 1 ? "" : "s"} reassigned to fallback assignee (case has no handler)
                                        </CRMParagraph>
                                    }
                                </SpacingColumn>
                            </Padding>
                        </>}

                        {reassignStatus === "failure" &&
                            <CRMParagraph>
                                <TextColor color={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}>
                                    Auto-reassign failed. Please try again.
                                </TextColor>
                            </CRMParagraph>
                        }

                        {canAutoHandle && reassignStatus !== "loading" &&
                            <Padding type="vertical" spacing={CRMSpacing.SMALL}>
                                <CRMButtonTertiaryColorable
                                    label="Auto-handle emails and roadblocks"
                                    textAlignment="center"
                                    textColor={CRMColors.NEUTRAL_PAPER}
                                    backgroundColor={CRMColors.PRIMARY_2}
                                    backgroundColorHover={CRMColors.PRIMARY_4}
                                    onClick={onAutoReassign}
                                />
                            </Padding>
                        }

                        {reassignStatus === "loading" && <>
                            <CRMParagraph>Reassigning...</CRMParagraph>
                            <CRMLoadingBar duration={3000} />
                        </>}
                    </Padding>

                    {/* Buttons */}
                    <SpacingRow spacing={"3px"} childSize="1fr 1fr">
                        <CRMButtonTertiaryColorable
                            label="Cancel"
                            textAlignment="center"
                            textColor={CRMColors.NEUTRAL_PAPER}
                            backgroundColor={CRMColors.NEUTRAL_2}
                            backgroundColorHover={CRMColors.NEUTRAL_0}
                            onClick={closePopup}
                        />
                        <CRMButtonTertiaryColorable
                            label={blocked ? "Cannot delete" : "Delete"}
                            textAlignment="center"
                            textColor={CRMColors.NEUTRAL_PAPER}
                            backgroundColor={blocked ? CRMColors.NEUTRAL_6 : CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}
                            backgroundColorHover={blocked ? CRMColors.NEUTRAL_6 : CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2}
                            disabled={blockerStatus !== "success" || blocked}
                            onClick={onConfirmDelete}
                        />
                    </SpacingRow>
                </SpacingColumn>
            </CRMBlockPopover>
        );
    };

    return (
        <CRMCardOutside borderRounding="right">
            <Padding spacing={CRMSpacing.LARGE}>
                <SpacingColumn spacing={CRMSpacing.LARGE}>

                    {pageStatus === "submitting" &&
                        <CRMLoadingBar duration={3000} />
                    }

                    {/* Active users grouped by company */}
                    {pipe(
                        activeUsersByCompany,
                        array.map(([company, users]) => (
                            <SpacingColumn key={company} spacing={CRMSpacing.MEDIUM}>
                                <CRMTitleSection>
                                    {snakeCaseToCopyText(company)}
                                </CRMTitleSection>

                                <SimpleGrid
                                    templateColumns="1fr 1fr"
                                    columnSpacing={CRMSpacing.MEDIUM}
                                    rowSpacing={CRMSpacing.MEDIUM}
                                >
                                    {pipe(
                                        users,
                                        array.map((user) => renderUserCard(user, false)),
                                    )}
                                </SimpleGrid>
                            </SpacingColumn>
                        )),
                    )}

                    {/* Deleted users */}
                    {deletedUsers.length > 0 &&
                        <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                            <CRMTitleSection>
                                Deleted Staff Users
                            </CRMTitleSection>

                            <SimpleGrid
                                templateColumns="1fr 1fr"
                                columnSpacing={CRMSpacing.MEDIUM}
                                rowSpacing={CRMSpacing.MEDIUM}
                            >
                                {pipe(
                                    deletedUsers,
                                    array.map((user) => renderUserCard(user, true)),
                                )}
                            </SimpleGrid>
                        </SpacingColumn>
                    }
                </SpacingColumn>
            </Padding>

            {renderDeletePopup()}
        </CRMCardOutside>
    );
};
